import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Page<TFilter> {
  page: number;
  pageSize: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  entity?: TFilter;
}

interface PageFilterContextProps<TFilter> {
  page: Page<TFilter>;
  setPage: (page: Page<TFilter>) => void;
  clearPage: () => void;
}

export function createPageFilterContext<TFilter>(storageKey: string): {
  Provider: React.FC<{ children: ReactNode }>;
  usePageFilter: () => PageFilterContextProps<TFilter>;
} {
  const Context = createContext<PageFilterContextProps<TFilter> | undefined>(undefined);

  const Provider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [page, setPageState] = useState<Page<TFilter>>(() => {
      const saved = sessionStorage.getItem(storageKey);
      return saved
        ? JSON.parse(saved)
        : { page: 1, pageSize: 10, order: 'DESC', sort: 'startDate' };
    });

    const setPage = (newPage: Page<TFilter>): void => {
      setPageState(newPage);
      sessionStorage.setItem(storageKey, JSON.stringify(newPage));
    };

    const clearPage = (): void => {
      sessionStorage.removeItem(storageKey);
      setPageState({ page: 1, pageSize: 10 });
    };

    return <Context.Provider value={{ page, setPage, clearPage }}>{children}</Context.Provider>;
  };

  const usePageFilter = (): PageFilterContextProps<TFilter> => {
    const context = useContext(Context);
    if (!context) {
      throw new Error('usePageFilter must be used within its Provider');
    }
    return context;
  };

  return { Provider, usePageFilter };
}
